import { useTranslation } from "react-i18next";
import type { i18n as I18n, TFunction } from "i18next";

/** Strings of the dealership and driving UI (own namespace, like the dock's). */
export const vehicleMessages = {
  en: {
    galeri: "Dealership",
    close: "Close",
    tabShop: "Showroom",
    tabMine: "My vehicles",
    wallet: "Balance",
    credits: "{{n}} credits",
    price: "Price",
    speed: "Top speed",
    accel: "Acceleration",
    seats: "{{n}} seats",
    color: "Color",
    colorName: "Color {{hex}}",
    buy: "Buy",
    buying: "Buying…",
    insufficient: "Not enough credits for this car.",
    failed: "That did not work. Try again.",
    bought: "Delivered to the bay: {{name}}",
    noneOwned: "You do not own a vehicle yet. Buy one in the showroom.",
    locate: "Locate",
    sell: "Sell",
    sellFor: "Sell for {{n}}",
    paint: "Repaint",
    drive: "Drive",
    parked: "Parked",
    driving: "Driving",
    odometer: "{{km}} km",
    pressE: "E – Get in",
    pressEGaleri: "E – Dealership",
    tapGetIn: "Get in",
    tapGaleri: "Open the dealership",
    exit: "Exit",
    exitHint: "E – Get out",
    gearD: "D",
    gearR: "R",
    gearN: "N",
    kmh: "km/h",
    gas: "Gas",
    brake: "Brake / reverse",
    handbrake: "Handbrake",
    camera: "Camera (6)",
    headlights: "Headlights",
    sound: "Engine sound",
    addCredits: "Add credits",
    topUpTitle: "Add credits",
    freeNow: "Free for now",
    freeNote: "No payment needed. Credits arrive instantly.",
    paidNote: "You pay on a secure payment page; credits arrive when it is done.",
    packagesLabel: "Packages",
    customAmount: "Custom amount",
    customHint: "{{min}} – {{max}} credits",
    topUpConfirm: "Add {{n}} credits",
    topUpConfirmPaid: "Pay {{price}} for {{n}} credits",
    toppedUp: "{{n}} credits added.",
    tabBuy: "Add",
    tabHistory: "History",
    historyEmpty: "No transactions yet.",
    topUpLimit: "You have reached today's top-up limit.",
    topUpUnavailable: "Payments are not available right now.",
    waitingPayment: "Waiting for your payment…",
    openCheckout: "Open the payment page",
    cancelWaiting: "Stop waiting",
    balanceNow: "Balance: {{n}} credits",
    entry: {
      topup: "Top-up",
      purchase: "Purchase",
      sale: "Sale",
      grant: "Gift",
    },
    class: {
      city: "City",
      economy: "Economy",
      sedan: "Sedan",
      suv: "SUV",
      sport: "Sport",
      van: "Van",
      bus: "Bus",
      bike: "Bike",
      car: "Car",
    },
  },
  tr: {
    galeri: "Galeri",
    close: "Kapat",
    tabShop: "Galeri",
    tabMine: "Araçlarım",
    wallet: "Bakiye",
    credits: "{{n}} kredi",
    price: "Fiyat",
    speed: "Azami hız",
    accel: "Hızlanma",
    seats: "{{n}} koltuk",
    color: "Renk",
    colorName: "Renk {{hex}}",
    buy: "Satın al",
    buying: "Satın alınıyor…",
    insufficient: "Bu araç için yeterli krediniz yok.",
    failed: "Bu işlem olmadı. Tekrar deneyin.",
    bought: "Teslimat alanında: {{name}}",
    noneOwned: "Henüz aracınız yok. Galeriden bir araç satın alın.",
    locate: "Bul",
    sell: "Sat",
    sellFor: "{{n}} krediye sat",
    paint: "Boya",
    drive: "Sür",
    parked: "Park halinde",
    driving: "Sürülüyor",
    odometer: "{{km}} km",
    pressE: "E – Bin",
    pressEGaleri: "E – Galeri",
    tapGetIn: "Bin",
    tapGaleri: "Galeriyi aç",
    exit: "İn",
    exitHint: "E – İn",
    gearD: "D",
    gearR: "R",
    gearN: "N",
    kmh: "km/s",
    gas: "Gaz",
    brake: "Fren / geri",
    handbrake: "El freni",
    camera: "Kamera (6)",
    headlights: "Farlar",
    sound: "Motor sesi",
    addCredits: "Kredi yükle",
    topUpTitle: "Kredi yükle",
    freeNow: "Şimdilik ücretsiz",
    freeNote: "Ödeme gerekmez. Krediler anında hesabınıza geçer.",
    paidNote: "Güvenli bir ödeme sayfasında ödersiniz; ödeme bitince krediler yüklenir.",
    packagesLabel: "Paketler",
    customAmount: "Özel miktar",
    customHint: "{{min}} – {{max}} kredi",
    topUpConfirm: "{{n}} kredi yükle",
    topUpConfirmPaid: "{{n}} kredi için {{price}} öde",
    toppedUp: "{{n}} kredi yüklendi.",
    tabBuy: "Yükle",
    tabHistory: "Geçmiş",
    historyEmpty: "Henüz hareket yok.",
    topUpLimit: "Bugünkü yükleme sınırına ulaştınız.",
    topUpUnavailable: "Ödeme şu anda kullanılamıyor.",
    waitingPayment: "Ödemeniz bekleniyor…",
    openCheckout: "Ödeme sayfasını aç",
    cancelWaiting: "Beklemeyi bırak",
    balanceNow: "Bakiye: {{n}} kredi",
    entry: {
      topup: "Yükleme",
      purchase: "Satın alma",
      sale: "Satış",
      grant: "Hediye",
    },
    class: {
      city: "Şehir",
      economy: "Ekonomik",
      sedan: "Sedan",
      suv: "SUV",
      sport: "Spor",
      van: "Minibüs",
      bus: "Otobüs",
      bike: "Bisiklet",
      car: "Araç",
    },
  },
} as const;

export const VEHICLES_NS = "worldVehicles";

function ensure(i18n: I18n): void {
  for (const [lng, bundle] of Object.entries(vehicleMessages)) {
    if (!i18n.hasResourceBundle(lng, VEHICLES_NS)) {
      i18n.addResourceBundle(lng, VEHICLES_NS, bundle, true, false);
    }
  }
}

export function useVehicleT(): TFunction<typeof VEHICLES_NS> {
  const { i18n } = useTranslation();
  ensure(i18n);
  return useTranslation(VEHICLES_NS).t;
}
